import React from 'react';

export default function DigitalGlobe({ size = 460 }) {
  // Connection arcs inside the globe (cx,cy in 400 viewBox)
  const arcs = [
    { d: 'M 80 220 Q 200 60 320 200', delay: '0s', color: '#1e6cb0' },
    { d: 'M 100 280 Q 200 150 310 250', delay: '0.6s', color: '#5eb0e8' },
    { d: 'M 120 150 Q 250 250 300 130', delay: '1.2s', color: '#1e6cb0' },
    { d: 'M 140 320 Q 240 180 290 260', delay: '1.8s', color: '#5eb0e8' },
    { d: 'M 200 60 Q 110 200 160 340', delay: '2.4s', color: '#1e6cb0' },
    { d: 'M 60 180 Q 200 280 340 160', delay: '3.0s', color: '#5eb0e8' },
    { d: 'M 70 100 Q 200 220 340 90', delay: '0.3s', color: '#5eb0e8' },
    { d: 'M 90 320 Q 220 200 330 320', delay: '0.9s', color: '#1e6cb0' },
    { d: 'M 220 70 Q 320 200 240 330', delay: '1.5s', color: '#5eb0e8' },
    { d: 'M 80 250 Q 180 100 280 320', delay: '2.1s', color: '#1e6cb0' },
    { d: 'M 130 90 Q 280 220 250 70', delay: '2.7s', color: '#5eb0e8' },
    { d: 'M 50 200 Q 200 350 350 200', delay: '3.3s', color: '#1e6cb0' },
  ];

  const dots = [
    { cx: 80, cy: 220, color: '#1e6cb0', delay: '0s' },
    { cx: 320, cy: 200, color: '#1e6cb0', delay: '0.3s' },
    { cx: 100, cy: 280, color: '#5eb0e8', delay: '0.6s' },
    { cx: 310, cy: 250, color: '#1e6cb0', delay: '0.9s' },
    { cx: 120, cy: 150, color: '#5eb0e8', delay: '1.2s' },
    { cx: 300, cy: 130, color: '#1e6cb0', delay: '1.5s' },
    { cx: 200, cy: 60, color: '#5eb0e8', delay: '1.8s' },
    { cx: 140, cy: 320, color: '#1e6cb0', delay: '2.1s' },
    { cx: 290, cy: 260, color: '#5eb0e8', delay: '2.4s' },
    { cx: 60, cy: 180, color: '#1e6cb0', delay: '2.7s' },
    { cx: 340, cy: 160, color: '#5eb0e8', delay: '3.0s' },
    { cx: 70, cy: 100, color: '#1e6cb0', delay: '3.3s' },
    { cx: 250, cy: 70, color: '#5eb0e8', delay: '3.6s' },
    { cx: 330, cy: 320, color: '#1e6cb0', delay: '3.9s' },
  ];

  // Italy / Biella marker (approx position on the globe surface)
  const biella = { cx: 218, cy: 168 };

  return (
    <div
      className="relative w-full h-full"
      style={
        typeof size === 'number'
          ? { width: size, height: size, maxWidth: '100%' }
          : { width: '100%', height: '100%' }
      }
    >
      {/* Soft glow background */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(circle at 35% 35%, rgba(94,176,232,0.35), rgba(30,108,176,0.05) 55%, transparent 70%)',
          filter: 'blur(12px)',
        }}
      />

      {/* OUTER ORBITS (rotating around the globe) */}
      <svg
        viewBox="0 0 480 480"
        className="absolute inset-0 w-full h-full overflow-visible"
        aria-hidden="true"
      >
        {/* Orbit ring 1 - clockwise */}
        <g className="orbit-spin-cw" style={{ transformOrigin: '240px 240px' }}>
          <ellipse
            cx="240"
            cy="240"
            rx="232"
            ry="80"
            fill="none"
            stroke="#1e6cb0"
            strokeWidth="1"
            opacity="0.35"
            strokeDasharray="2 6"
          />
          <circle cx="472" cy="240" r="5" fill="#1e6cb0">
            <animate attributeName="opacity" values="1;0.4;1" dur="2.4s" repeatCount="indefinite" />
          </circle>
          <circle cx="8" cy="240" r="3.5" fill="#5eb0e8" opacity="0.7" />
        </g>

        {/* Orbit ring 2 - tilted, counter-clockwise */}
        <g className="orbit-spin-ccw" style={{ transformOrigin: '240px 240px', transform: 'rotate(35deg)' }}>
          <ellipse
            cx="240"
            cy="240"
            rx="225"
            ry="60"
            fill="none"
            stroke="#5eb0e8"
            strokeWidth="1"
            opacity="0.4"
            strokeDasharray="3 5"
          />
          <circle cx="465" cy="240" r="4" fill="#5eb0e8">
            <animate attributeName="opacity" values="0.5;1;0.5" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle cx="15" cy="240" r="3" fill="#1e6cb0" opacity="0.7" />
        </g>

        {/* Orbit ring 3 - more tilted, clockwise slow */}
        <g className="orbit-spin-cw-slow" style={{ transformOrigin: '240px 240px', transform: 'rotate(-25deg)' }}>
          <ellipse
            cx="240"
            cy="240"
            rx="220"
            ry="100"
            fill="none"
            stroke="#1e6cb0"
            strokeWidth="0.8"
            opacity="0.3"
          />
          <circle cx="460" cy="240" r="3" fill="#1e6cb0" opacity="0.8" />
        </g>

        {/* Orbit ring 4 - vertical-ish */}
        <g className="orbit-spin-ccw-slow" style={{ transformOrigin: '240px 240px', transform: 'rotate(75deg)' }}>
          <ellipse
            cx="240"
            cy="240"
            rx="218"
            ry="48"
            fill="none"
            stroke="#5eb0e8"
            strokeWidth="0.8"
            opacity="0.35"
            strokeDasharray="1 4"
          />
          <circle cx="458" cy="240" r="3.5" fill="#5eb0e8" opacity="0.85" />
        </g>
      </svg>

      {/* Globe sphere with rotating meridians */}
      <svg
        viewBox="0 0 400 400"
        className="absolute inset-0 w-full h-full globe-spin-slow"
        style={{ padding: '8%' }}
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="sphere" cx="38%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="45%" stopColor="#dbeafe" />
            <stop offset="100%" stopColor="#bfdbfe" />
          </radialGradient>
        </defs>

        {/* Sphere fill */}
        <circle cx="200" cy="200" r="180" fill="url(#sphere)" opacity="0.55" />
        <circle cx="200" cy="200" r="180" fill="none" stroke="#1e6cb0" strokeWidth="1" opacity="0.55" />

        {/* Latitude lines */}
        <g stroke="#5eb0e8" strokeWidth="0.8" fill="none" opacity="0.55">
          <line x1="20" y1="200" x2="380" y2="200" />
          <ellipse cx="200" cy="200" rx="180" ry="40" />
          <ellipse cx="200" cy="200" rx="180" ry="80" />
          <ellipse cx="200" cy="200" rx="180" ry="120" />
          <ellipse cx="200" cy="200" rx="180" ry="155" opacity="0.4" />
        </g>

        {/* Longitude lines */}
        <g stroke="#1e6cb0" strokeWidth="0.8" fill="none" opacity="0.45">
          <line x1="200" y1="20" x2="200" y2="380" />
          <ellipse cx="200" cy="200" rx="40" ry="180" />
          <ellipse cx="200" cy="200" rx="80" ry="180" />
          <ellipse cx="200" cy="200" rx="120" ry="180" />
          <ellipse cx="200" cy="200" rx="155" ry="180" opacity="0.4" />
        </g>
      </svg>

      {/* Animated arcs and pulsing dots */}
      <svg
        viewBox="0 0 400 400"
        className="absolute inset-0 w-full h-full"
        style={{ padding: '8%' }}
        aria-hidden="true"
      >
        {/* Network arcs that draw and fade */}
        <g fill="none" strokeLinecap="round">
          {arcs.map((a, i) => (
            <path
              key={i}
              d={a.d}
              stroke={a.color}
              strokeWidth="1.6"
              className="globe-arc"
              style={{ animationDelay: a.delay }}
            />
          ))}
        </g>

        {/* Pulsing dots */}
        {dots.map((d, i) => (
          <g key={i} style={{ transformOrigin: `${d.cx}px ${d.cy}px` }}>
            <circle
              cx={d.cx}
              cy={d.cy}
              r="6"
              fill={d.color}
              opacity="0.25"
              className="globe-pulse"
              style={{ animationDelay: d.delay }}
            />
            <circle cx={d.cx} cy={d.cy} r="2.5" fill={d.color} />
          </g>
        ))}

        {/* Biella highlighted marker */}
        <g style={{ transformOrigin: `${biella.cx}px ${biella.cy}px` }}>
          <circle cx={biella.cx} cy={biella.cy} r="10" fill="#1e6cb0" opacity="0.18" className="globe-pulse-strong" />
          <circle cx={biella.cx} cy={biella.cy} r="5" fill="#1e6cb0" />
          <circle cx={biella.cx} cy={biella.cy} r="2" fill="#ffffff" />
        </g>
      </svg>

      {/* Floating label for Biella */}
      <div
        className="absolute pointer-events-none"
        style={{ left: '54.5%', top: '38%' }}
      >
        <div className="px-2.5 py-1 rounded-full bg-white border border-[#1e6cb0]/20 shadow-[0_4px_14px_-4px_rgba(30,108,176,0.35)] text-[10px] tracking-[0.2em] uppercase font-bold text-[#1e6cb0]">
          Biella
        </div>
      </div>
    </div>
  );
}
